import PortalDialog, {PortalDialogProperties, PortalDialogState} from "../PortalDialog";
import React, {ReactNode} from "react";
import {Dialog} from "primereact/dialog";
import PortalDialogType from "../../PortalDialogType";
import MimeTypes from "../../../document/mimetype/MimeTypes";
import {WebServiceType, WebServiceTypes} from "@softvision/webpdf-wsclient-9-typescript";
import {Dropdown} from "primereact/dropdown";
import MimeTypeFilter from "../../../document/mimetype/MimeTypeFilter";
import Parameter from "../../parameter/Parameter";
import PortalDocument from "../../../document/PortalDocument";
import {Button} from "primereact/button";
import PortalDocumentManager from "../../../document/PortalDocumentManager";
import DialogDefault from "../../parameter/PortalDialogDefault";
import MimeType from "../../../document/mimetype/MimeType";
import Message from "../../../../shared/log/Message";
import {MessageType} from "../../../../shared/log/MessageInterface";
import {
	AdminWebserviceStatus,
	BaseToolbox,
	Functions,
	Information,
	OrientationDetection,
	PageOrientation,
	Security,
	Settings,
	SettingsInterface,
	SignatureEditProfile,
	ToolboxRotateRotate,
} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import OrientationDialogFields from "../../field/page/OrientationDialogFields";
import PortalDialogSecurity, {Intention} from "../../PortalDialogSecurity";
import Translate from "../../../../shared/translation/Translate";
import Helper from "../../../../shared/components/Helper";
import Icon from "../../../../shared/components/Icon";
import FunctionHelper from "../../../../shared/helper/FunctionHelper";

export class OrientationDialogDefinition extends PortalDialog<ToolboxRotateRotate,
	PortalDialogProperties<ToolboxRotateRotate>, PortalDialogState<ToolboxRotateRotate>> {
	public static isEnabled(portalInfo: Information): boolean {
		return FunctionHelper.applyValue(
			portalInfo.webserviceStatus?.webservices?.toolbox?.enabled, AdminWebserviceStatus.getEnabledDefault()
		) && FunctionHelper.applyValue(
			portalInfo.functionsStatus?.rotatePages, Functions.getRotatePagesDefault()
		);
	}

	public static isActive(
		selectedDocuments: Array<PortalDocument>, portalInfo: Information, signatureEditProfile: SignatureEditProfile
	): boolean {
		let mimeTypes: Array<MimeType> = [];

		for (let selectedDocument of selectedDocuments) {
			mimeTypes.push(selectedDocument.getMimeType());

			if (selectedDocument.isLocked()) {
				return false;
			}

			if (!PortalDialogSecurity.checkRestrictions(
				PortalDialogType.ORIENTATION, signatureEditProfile, Intention.editingContent, selectedDocument
			)) {
				return false;
			}

			if (!selectedDocument.isEditable()) {
				return false;
			}
		}

		return this.isEnabled(portalInfo) && selectedDocuments.length === 1 &&
			MimeTypeFilter.checkSelection(mimeTypes, [MimeTypes.PDF]);
	}

	public renderDialog(): ReactNode {
		let classList: Array<string> = ["orientation-dialog"];
		if (this.state.submitted) {
			classList.push("submitted");
		}

		return (
			<Dialog className={classList.join(" ")} visible={this.props.visible} onHide={this.hide}
					header={this.renderHeader()} footer={this.renderFooter()} resizable={false}
					appendTo={document.getElementById("root")}>
				<div className="p-grid">
					{this.renderError()}
					<Helper {...this.props}>
						<Translate {...this.props} data={"dialog.orientation.general"} namespace={"help"}
								   markdown={true}/>
					</Helper>
					{this.renderContents()}
				</div>
			</Dialog>
		);
	}

	public renderContents(): ReactNode {
		let parameter: ToolboxRotateRotate | undefined = this.state.parameter?.data;

		if (!FunctionHelper.exists(parameter)) {
			return null;
		}

		return (
			<>
				<fieldset className="p-col-12 p-grid">
					<div className="p-col-12 p-grid"
						 hidden={!this.state.fields?.contains(OrientationDialogFields.DIRECTION)}>
						<label className="p-col-12 p-sm-4" htmlFor="control-direction">
							<Translate {...this.props} data="orientation.direction.label" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-8 p-grid">
							<Dropdown inputId="control-direction"
									  value={
										  parameter?.pageOrientation === PageOrientation.Portrait ?
											  PageOrientation.Landscape : PageOrientation.Portrait
									  }
									  options={[
										  {
											  label: Translate.value(
												  "orientation.direction.portrait", {ns: "dialog"}
											  ),
											  value: PageOrientation.Portrait
										  },
										  {
											  label: Translate.value(
												  "orientation.direction.landscape", {ns: "dialog"}
											  ),
											  value: PageOrientation.Landscape
										  },
									  ]}
									  onChange={(e) => this.onChange(
										  OrientationDialogFields.DIRECTION, e.value
									  )}
									  appendTo={document.getElementById("root")}/>
						</div>
					</div>
					<div className="p-col-12 p-grid"
						 hidden={!this.state.fields?.contains(OrientationDialogFields.ORIENTATION)}>
						<label className="p-col-12 p-sm-4" htmlFor="control-orientation">
							<Translate {...this.props} data="orientation.orientation.label" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-8 p-grid">
							<Dropdown inputId="control-orientation"
									  value={FunctionHelper.applyValue(
										  parameter?.orientationDetectMode,
										  ToolboxRotateRotate.getOrientationDetectModeDefault()
									  )}
									  options={[
										  {
											  label: Translate.value(
												  "orientation.orientation.value", {ns: "dialog"}
											  ),
											  value: OrientationDetection.Value
										  },
										  {
											  label: Translate.value(
												  "orientation.orientation.dimensions", {ns: "dialog"}
											  ),
											  value: OrientationDetection.Dimensions
										  }
									  ]}
									  onChange={(e) => this.onChange(
										  OrientationDialogFields.ORIENTATION, e.value
									  )}
									  appendTo={document.getElementById("root")}/>
						</div>
					</div>
				</fieldset>
			</>
		);
	}

	protected onChange(field: OrientationDialogFields, value: any): void {
		let parameter: ToolboxRotateRotate = this.state.parameter!.data;

		switch (field) {
			case OrientationDialogFields.DIRECTION:
				switch (value) {
					case PageOrientation.Portrait:
						parameter.pageOrientation = PageOrientation.Landscape;
						parameter.degrees = 270;
						break;
					case PageOrientation.Landscape:
						parameter.pageOrientation = PageOrientation.Portrait;
						parameter.degrees = 90;
						break;
				}
				break;
			case OrientationDialogFields.ORIENTATION:
				parameter.orientationDetectMode = value;
				break;
		}

		this.setState({
			parameter: this.state.parameter
		});
	}

	protected renderHeader(): ReactNode {
		return (
			<div>
				<h1>
					<Icon icon={"icon-orientation small"}/>
					<Translate {...this.props} data="orientation.title" namespace="dialog"/>
				</h1>
				<Button icon="icon icon-close" onClick={() => this.hide()}/>
				{this.renderHelpButton()}
			</div>
		);
	}

	protected isEditPasswordNeeded(portalDocument: PortalDocument): boolean {
		return super.isEditPasswordNeeded(portalDocument) && !FunctionHelper.applyValue(
			portalDocument.metadata?.security?.canAssemble, Security.getCanAssembleDefault()
		);
	}

	protected async submit(): Promise<void> {
		await super.submit();

		let toolboxOperation: BaseToolbox = BaseToolbox.fromJson({
			rotate: this.state.parameter!.data.toJson()
		});

		let operationData: Array<BaseToolbox> = [toolboxOperation];
		let webServiceType: WebServiceType = WebServiceTypes.TOOLBOX;
		let documentManager: PortalDocumentManager = this.props.context.documentManager;
		let selection: Array<PortalDocument> = documentManager.getSelection();

		for (let portalDocument of selection) {
			try {
				await this.props.context.session.executeRequest({
					webServiceType: webServiceType,
					document: portalDocument,
					operation: operationData,
					settings: Settings.fromJson({
						pdfa: {
							pdfaEditProfile: this.props.pdfaEditProfile
						},
						signature: {
							signatureEditProfile: this.props.signatureEditProfile
						},
						compress: {
							compressProfile: this.props.compressProfile,
							compressObjects: {
								objectStreamSize: this.props.objectStreamSize
							}
						}
					} as SettingsInterface)
				});
			} catch (exception: any) {
				if (await this.onError(exception)) {
					await this.submit();
				}
				return;
			}

			let message: Message = new Message("success.toolbox.orientation", MessageType.SUCCESS);
			message.context = {filename: '"' + portalDocument.getFileName() + '" '};
			this.props.context.getPage().addMessage(message);
			this.hide();
		}
	}

	protected getDialogType(): PortalDialogType {
		return PortalDialogType.ORIENTATION;
	}

	protected getDefaultParameter(): Parameter<ToolboxRotateRotate> {
		return DialogDefault.getParameter(
			this.getDialogType(), this.props.context.documentManager.getSelection()
		);
	}
}

const OrientationDialog = React.memo(OrientationDialogDefinition,
	(
		prevProps: Readonly<PortalDialogProperties<ToolboxRotateRotate>>,
		nextProps: Readonly<PortalDialogProperties<ToolboxRotateRotate>>
	) => {
		if (!nextProps.visible && !prevProps.visible) {
			return true
		}

		return false;
	}
);

export default OrientationDialog;